import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import colors from '@/assets/scss/_themes-vars.module.scss'
import { ProposalTarget, TranscriptionProposalItem } from '@/core/models/transcription'
import { useApplyStructuredDataMutation, useProposeStructuredDataMutation } from '@/core/services/transcription'
import { getApiErrorMessage } from '@/utils/errors'

export type StructuredDataReviewDialogProps = {
  open: boolean
  transcriptionId?: string
  consultationId: string
  onClose: () => void
}

type ReviewRow = TranscriptionProposalItem & { checked: boolean }

const TARGET_KEY: Record<ProposalTarget, string> = {
  'consultation.reason': 'reason',
  'consultation.comment': 'comment',
  'consultation.mesure': 'mesure',
  'consultation.biological': 'biological',
  'patient.background': 'background',
  'patient.allergy': 'allergy',
  'patient.treatment': 'treatment',
  'patient.vaccins': 'vaccins',
}

/**
 * Review step of "Add to structured data": ELARA's proposals, one row each, all ticked by
 * default, values editable. Nothing is written until "Apply".
 */
export function StructuredDataReviewDialog({ open, transcriptionId, consultationId, onClose }: StructuredDataReviewDialogProps) {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const [propose, { isLoading: isProposing, error: proposeError }] = useProposeStructuredDataMutation()
  const [apply, { isLoading: isApplying }] = useApplyStructuredDataMutation()
  const [rows, setRows] = useState<ReviewRow[]>([])
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (!open || !transcriptionId) return
    setLoaded(false)
    setRows([])
    propose({ transcriptionId })
      .unwrap()
      .then((proposals) => setRows(proposals.items.map((item) => ({ ...item, checked: true }))))
      .catch(() => undefined)
      .finally(() => setLoaded(true))
  }, [open, transcriptionId, propose])

  const groups = useMemo(
    () => [
      { key: 'consultation', rows: rows.filter((row) => row.target.startsWith('consultation.')) },
      { key: 'patient', rows: rows.filter((row) => row.target.startsWith('patient.')) },
    ],
    [rows]
  )
  const selected = rows.filter((row) => row.checked && row.value.trim())

  const updateRow = (id: string, patch: Partial<ReviewRow>) => setRows((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row)))

  const handleApply = async () => {
    if (!transcriptionId || !selected.length) return
    try {
      const result = await apply({ transcriptionId, consultationId, items: selected.map(({ checked, ...item }) => item) }).unwrap()
      enqueueSnackbar(t('page.liveConsultation.transcription.review.applied', { count: result.applied }), { variant: 'success' })
      onClose()
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('page.liveConsultation.transcription.review.applyError')), { variant: 'error' })
    }
  }

  return (
    <Dialog open={open} onClose={isApplying ? undefined : onClose} maxWidth="md" fullWidth data-testid="structured-data-review-dialog">
      <DialogTitle>{t('page.liveConsultation.transcription.review.title')}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ color: colors.gray500, mb: 2 }}>
          {t('page.liveConsultation.transcription.review.subtitle')}
        </Typography>

        {isProposing && (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 4, justifyContent: 'center' }}>
            <CircularProgress size={20} />
            <Typography variant="body2">{t('page.liveConsultation.transcription.review.loading')}</Typography>
          </Stack>
        )}

        {!isProposing && proposeError && (
          <Alert severity="error">{getApiErrorMessage(proposeError, t('page.liveConsultation.transcription.review.error'))}</Alert>
        )}

        {!isProposing && loaded && !proposeError && !rows.length && (
          <Alert severity="info">{t('page.liveConsultation.transcription.review.empty')}</Alert>
        )}

        {!isProposing &&
          groups
            .filter((group) => group.rows.length)
            .map((group) => (
              <Box key={group.key} sx={{ mb: 2 }}>
                <Divider sx={{ mb: 1.5 }}>{t(`page.liveConsultation.transcription.review.group.${group.key}`)}</Divider>
                <Stack spacing={1.5}>
                  {group.rows.map((row) => (
                    <Stack key={row.id} direction="row" spacing={1} alignItems="flex-start" data-testid="proposal-row">
                      <Checkbox checked={row.checked} onChange={(event) => updateRow(row.id, { checked: event.target.checked })} sx={{ mt: 0.5 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                          {t(`page.liveConsultation.transcription.review.target.${TARGET_KEY[row.target]}`)}
                          {row.target === 'consultation.mesure' || row.target === 'consultation.biological' ? ` · ${row.label}` : ''}
                          {row.unit ? ` (${row.unit})` : ''}
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          multiline={row.target === 'consultation.comment' || row.target.startsWith('patient.')}
                          value={row.value}
                          disabled={!row.checked}
                          onChange={(event) => updateRow(row.id, { value: event.target.value })}
                        />
                        {!!row.currentValue && (
                          <Typography variant="caption" sx={{ color: colors.gray500, display: 'block', mt: 0.5 }}>
                            {t('page.liveConsultation.transcription.review.current')}: {row.currentValue}
                          </Typography>
                        )}
                        {!!row.evidence && (
                          <Typography variant="caption" sx={{ color: colors.gray500, display: 'block', fontStyle: 'italic' }}>
                            {t('page.liveConsultation.transcription.review.evidence')}: « {row.evidence} »
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            ))}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={isApplying}>
          {t('page.liveConsultation.transcription.review.cancel')}
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          disabled={!selected.length || isApplying || isProposing}
          startIcon={isApplying ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('page.liveConsultation.transcription.review.apply', { count: selected.length })}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
